import TextResolver from 'nucleus-text/TextResolver';
import safeHtmlDecode from '../../utils/safeHtmlDecode';
// ACTION TYPES
export const SET_LOCALE = 'nucleus/text/SET_LOCALE';
const REGISTER_TRANSLATION = 'nucleus/text/REGISTER_TRANSLATION';
const REGISTER_DATATAGS = 'nucleus-text/text/REGISTER_DATATAGS';
const SET_CURRENCY_CODE = 'nucleus/text/SET_CURRENCY_CODE';
const REFERSH_RESOLVERS = 'nucleus-text/text/REFERSH_RESOLVERS';
// ACTIONS.
export function setLocale(locale, context = 'default') {
    return {
        type: SET_LOCALE,
        payload: {
            locale,
            context
        }
    };
}
export function registerTranslation(translations, locale, context = 'default') {
    return {
        type: REGISTER_TRANSLATION,
        payload: {
            translations,
            locale,
            context
        }
    };
}
export function registerDatatags(datatags, locale, context = 'default') {
    return {
        type: REGISTER_DATATAGS,
        payload: {
            datatags,
            locale,
            context
        }
    };
}
export function setCurrencyCode(currencyCode, context = 'default') {
    return {
        type: SET_CURRENCY_CODE,
        payload: {
            currencyCode,
            context
        }
    };
}
export function refreshResolvers() {
    return {
        type: REFERSH_RESOLVERS
    };
}
/**
 * Function to retrieve datatag resolutions from somewhere. For site editors, it is expected to pull out
 * resolutions from the datatagConfig provided on state. For guest site implementations, it is expected to
 * call the fetchTextResolverDatatags provided on state
 * @param {*} args
 */
function getDatatagResolutions(dispatch, getState, datatags, options) {
    const { fetchTextResolverDatatags, datatagConfig } = getState();
    // If no datatags, just return;
    if (!datatags || !datatags.length) {
        return;
    }
    // Decode any HTML entities in the datatag strings.
    const htmlDecodedDatatags = datatags.map(tag => safeHtmlDecode(tag));
    // Guest sides will generally define a function on state to retrieve datatag resolutions.
    if (fetchTextResolverDatatags) {
        fetchTextResolverDatatags(dispatch, getState, htmlDecodedDatatags, options);
        return;
    }
    // Site editors will generally load example text via the datatagConfig.
    if (datatagConfig && datatagConfig.getDatatagExampleResolution) {
        const { successCallback, translate } = options;
        const resolutions = htmlDecodedDatatags.map(tag => datatagConfig.getDatatagExampleResolution(datatagConfig, tag, translate));
        successCallback(resolutions);
    }
}
/**
 * Fetches resolutions for any datatags that have been recognized by the given text resolver but have not had their
 * resolutions retrieved yet.
 * @param {*} textResolver - The text resolver backing the text reducer you want to refresh datatags for.
 */
export function fetchDatatagResolutions(textResolver) {
    return (dispatch, getState) => {
        textResolver.fetchDataTags((datatags, options) => {
            getDatatagResolutions(dispatch, getState, datatags, options);
        }, () => {
            dispatch(refreshResolvers());
        });
    };
}
/**
 * Fetches resolutions for all datatags that have been recognized by the given text resolver, whether it already
 * has resolutions for them or not.
 * @param {*} textResolver - The text resolver backing the text reducer you want to refresh datatags for.
 */
export function fetchAllDatatagResolutions(textResolver) {
    return (dispatch, getState) => {
        textResolver.fetchAllDataTags((datatags, options) => {
            getDatatagResolutions(dispatch, getState, datatags, options);
        }, () => {
            dispatch(refreshResolvers());
        });
    };
}
/* eslint-disable no-param-reassign*/
/**
 * We set renderId properties on the translate functions to do special function comparisons in certain places.
 * For example, in the shared WidgetRenderer component to switch out the normal translate prop with the datatag
 * enabled translate function.
 * @param {*} textResolver
 */
function setTranslateRenderIds(textResolver) {
    textResolver.resolveTranslation.renderId = `${textResolver.context}-resolveTranslation`;
    textResolver.resolveText.renderId = `${textResolver.context}-resolveText`;
    return textResolver;
}
/* eslint-enable no-param-reassign*/
/**
 * Helper function to copy the given text resolver.
 * @param {*} textResolver
 */
function copyTextResolver(textResolver) {
    return setTranslateRenderIds(textResolver.copy());
}
/**
 * Helper function to create a new text resolver.
 * @param {*} context
 * @param logicTags    logicTag object containing logicTagExampleText and logicTagRegularExpression
 */
function createTextResolver(context, logicTags = {}, overrideFormats = {}) {
    const textResolver = new TextResolver({
        locale: 'en-US',
        context,
        translationFallback: (context_, locale, key) => {
            return key;
        },
        logicTagExampleText: logicTags === null || logicTags === void 0 ? void 0 : logicTags.logicTagExampleText,
        logicTagRegularExpression: logicTags === null || logicTags === void 0 ? void 0 : logicTags.logicTagRegularExpression,
        overrideFormats
    });
    return setTranslateRenderIds(textResolver);
}
export function createTextReducer(context = 'default', datatags = {}, logicTags = {}, overrideFormats = {}) {
    const { enabled: enableDatatags } = datatags;
    const textResolver = createTextResolver(context, logicTags, overrideFormats);
    return (state = {
        translate: textResolver.resolveTranslation,
        translateWithDatatags: enableDatatags && textResolver.resolveText,
        locale: textResolver.locale,
        resolver: textResolver,
        translateDate: textResolver.date,
        translateTime: textResolver.time,
        translateDateTime: textResolver.dateTime
    }, action) => {
        switch (action.type) {
            case SET_LOCALE:
                {
                    if (state.resolver.context !== action.payload.context) {
                        return state;
                    }
                    const newResolver = copyTextResolver(state.resolver);
                    newResolver.setLocale(action.payload.locale);
                    return Object.assign({}, {
                        translate: newResolver.resolveTranslation,
                        translateWithDatatags: enableDatatags && newResolver.resolveText,
                        translateDate: newResolver.date,
                        translateTime: newResolver.time,
                        translateDateTime: newResolver.dateTime,
                        locale: newResolver.locale,
                        resolver: newResolver
                    });
                }
            case REGISTER_TRANSLATION:
                {
                    if (state.resolver.context !== action.payload.context) {
                        return state;
                    }
                    const newResolver = copyTextResolver(state.resolver);
                    newResolver.registerTranslations(action.payload.translations, null, action.payload.locale);
                    return Object.assign({}, {
                        translate: newResolver.resolveTranslation,
                        translateWithDatatags: enableDatatags && newResolver.resolveText,
                        translateDate: newResolver.date,
                        translateTime: newResolver.time,
                        translateDateTime: newResolver.dateTime,
                        locale: newResolver.locale,
                        resolver: newResolver
                    });
                }
            case REGISTER_DATATAGS:
                {
                    if (state.resolver.context !== action.payload.context) {
                        return state;
                    }
                    const newResolver = copyTextResolver(state.resolver);
                    newResolver.registerDataTags(action.payload.datatags, null, action.payload.locale);
                    return Object.assign({}, {
                        translate: newResolver.resolveTranslation,
                        translateWithDatatags: enableDatatags && newResolver.resolveText,
                        translateDate: newResolver.date,
                        translateTime: newResolver.time,
                        translateDateTime: newResolver.dateTime,
                        locale: newResolver.locale,
                        resolver: newResolver
                    });
                }
            case SET_CURRENCY_CODE:
                {
                    if (state.resolver.context !== action.payload.context) {
                        return state;
                    }
                    const newResolver = copyTextResolver(state.resolver);
                    newResolver.currencyCode = action.payload.currencyCode;
                    return Object.assign({}, {
                        translate: newResolver.resolveTranslation,
                        translateWithDatatags: enableDatatags && newResolver.resolveText,
                        translateDate: newResolver.date,
                        translateTime: newResolver.time,
                        translateDateTime: newResolver.dateTime,
                        locale: newResolver.locale,
                        resolver: newResolver
                    });
                }
            case REFERSH_RESOLVERS:
                {
                    const newResolver = copyTextResolver(state.resolver);
                    return Object.assign({}, {
                        translate: newResolver.resolveTranslation,
                        translateWithDatatags: enableDatatags && newResolver.resolveText,
                        translateDate: newResolver.date,
                        translateTime: newResolver.time,
                        translateDateTime: newResolver.dateTime,
                        locale: newResolver.locale,
                        resolver: newResolver
                    });
                }
            default:
                return state;
        }
    };
}
// REDUCER
/**
 * The text reducer manages a TextResolver instance at the application
 * level and also provides shortcuts to its necessary functions that the
 * application needs to use.
 */
const reducer = createTextReducer();
export default reducer;
